import { useState, useEffect, useCallback, useMemo } from "react";

import Header from "../../../../components/Layout/Header";
import Footer from "../../../../components/Layout/Footer";
import Sidebar from "../../../../components/Layout/Sidebar";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";

import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";

import {
  getData,
  deleteData,
} from "../../../../services/event/presentations/presentationSetupService";

/* ── Styles ─────────────────────────────────────────── */
const S = {
  page: { background: "#f4f6f9", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" },
  container: { padding: "28px" },
  topBar: {
    display: "flex", alignItems: "flex-start",
    justifyContent: "space-between", marginBottom: 24,
    flexWrap: "wrap", gap: 12,
  },
  breadcrumb: {
    fontSize: 12, color: "#9aa5b4", fontWeight: 600,
    letterSpacing: 1, textTransform: "uppercase", marginBottom: 3,
  },
  pageTitle: { fontSize: 22, fontWeight: 700, color: "#1a2332", margin: 10 },
  addBtn: {
    display: "inline-flex", alignItems: "center", gap: 7,
    padding: "9px 20px", background: "#1a6b3c", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600,
    cursor: "pointer", transition: "all 0.18s", fontFamily: "'DM Sans', sans-serif",
  },
  tableCard: {
    background: "#fff", borderRadius: 14, border: "1px solid #e3e8ef",
    overflow: "hidden", boxShadow: "0 2px 12px rgba(26,107,60,0.06)",
  },
  tableToolbar: {
    padding: "14px 18px", borderBottom: "1px solid #e3e8ef",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft: { display: "flex", alignItems: "center", gap: 10 },
  tableTitle: { fontSize: 14, fontWeight: 700, color: "#1a2332" },
  countBadge: {
    background: "#e8f5ee", color: "#1a6b3c",
    fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
  },
  dayBadge: (day) => ({
    display: "inline-block",
    padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: day === "1" || day === 1 ? "#dbeafe" : "#fce7f3",
    color:      day === "1" || day === 1 ? "#1d4ed8" : "#be185d",
  }),
  categoryBadge: (cat) => {
    const map = {
      KEYNOTE: { bg: "#fff3cd", color: "#8a6d00" },
      ORAL:    { bg: "#dbeafe", color: "#1d4ed8" },
      SPEAKER: { bg: "#e8f5ee", color: "#1a6b3c" },
      "E-POSTER": { bg: "#ede9fe", color: "#5b21b6" },
      VIDEO:   { bg: "#fff1f0", color: "#c0392b" },
    };
    const style = map[cat?.toUpperCase()] || { bg: "#f0f4f8", color: "#6b7a8d" };
    return {
      display: "inline-block", padding: "2px 9px",
      borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: style.bg, color: style.color,
    };
  },
  searchWrap: { position: "relative", width: 260 },
  searchIcon: {
    position: "absolute", left: 10, top: "50%",
    transform: "translateY(-50%)", color: "#9aa5b4",
    fontSize: 14, pointerEvents: "none",
  },
  searchInput: {
    width: "100%", padding: "8px 12px 8px 34px",
    border: "1.5px solid #e3e8ef", borderRadius: 8,
    fontSize: 13, outline: "none",
    fontFamily: "'DM Sans', sans-serif", color: "#1a2332",
    transition: "border-color 0.15s",
  },
  editBtn: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 12px", background: "#fff8e6", color: "#b07d1a",
    border: "1.5px solid #f0d080", borderRadius: 6, fontSize: 12,
    fontWeight: 600, cursor: "pointer", marginRight: 6,
    transition: "all 0.15s", fontFamily: "'DM Sans', sans-serif",
  },
  deleteBtn: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 12px", background: "#fff1f0", color: "#c0392b",
    border: "1.5px solid #f5c6c3", borderRadius: 6, fontSize: 12,
    fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
    fontFamily: "'DM Sans', sans-serif",
  },
  emptyState: { textAlign: "center", padding: "56px 20px", color: "#9aa5b4" },
  deleteIcon: {
    width: 52, height: 52, background: "#fff1f0",
    border: "1.5px solid #f5c6c3", borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto 16px", fontSize: 22,
  },
  modalActionRow: { display: "flex", gap: 10, justifyContent: "center" },
  modalCancelBtn: {
    padding: "9px 22px", border: "1.5px solid #e3e8ef",
    borderRadius: 8, background: "#fff", color: "#4a5568",
    fontWeight: 600, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit",
  },
  modalDeleteBtn: {
    padding: "9px 22px", border: "none", borderRadius: 8,
    background: "#c0392b", color: "#fff",
    fontWeight: 600, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit",
  },
};

const tableStyles = {
  headRow: {
    style: { background: "#f8fafb", borderBottom: "2px solid #e3e8ef", minHeight: 44 },
  },
  headCells: {
    style: {
      fontSize: 11, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: 0.5, color: "#6b7a8d",
      fontFamily: "'DM Sans', sans-serif", paddingLeft: 16, paddingRight: 16,
    },
  },
  rows: {
    style: {
      fontSize: 13.5, fontFamily: "'DM Sans', sans-serif",
      borderBottom: "1px solid #f0f4f8", minHeight: 54,
      "&:hover": { background: "#f5fbf7" },
    },
  },
  cells: { style: { paddingLeft: 16, paddingRight: 16 } },
  pagination: {
    style: {
      borderTop: "1px solid #e3e8ef", fontSize: 13,
      fontFamily: "'DM Sans', sans-serif", color: "#4a5568",
    },
  },
};

/* ══════════════════════════════════════════════════════════════ */
const PresentationSetupList = () => {
  const navigate = useNavigate();
  const [dataList,    setDataList]    = useState([]);
  const [showDelete,  setShowDelete]  = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchText,  setSearchText]  = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [dayFilter,   setDayFilter]   = useState("");
  const [catFilter,   setCatFilter]   = useState("");

  const fetchData = useCallback(async () => {
    try {
      const data = await getData();
      setDataList(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    };
    run();
  }, [fetchData]);

  const handleDelete = async () => {
    if (!selectedRow) return;
    setDataList((p) => p.filter((d) => d.id !== selectedRow.id));
    await deleteData(selectedRow);
    setShowDelete(false);
  };

  const filteredData = useMemo(() =>
    dataList
      .filter((d) => {
        const matchSearch =
          !searchText ||
          d.presentation_title?.toLowerCase().includes(searchText.toLowerCase());
        const matchDay = !dayFilter || String(d.day) === String(dayFilter);
        const matchCat = !catFilter || d.category?.toUpperCase() === catFilter;
        return matchSearch && matchDay && matchCat;
      })
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    [dataList, searchText, dayFilter, catFilter]
  );

  /* unique categories for filter chips */
  const availableCategories = useMemo(() =>
    [...new Set(dataList.map((d) => d.category?.toUpperCase()).filter(Boolean))],
    [dataList]
  );

  const handleAddPresentation = useCallback(() => {
    navigate("/presentationSetup/create");
  }, [navigate]);

  const handleEditPresentation = useCallback((row) => {
    navigate(`/presentationSetup/${row.id}`);
  }, [navigate]);

  const columns = [
    {
      name: "#",
      selector: (_, i) => (currentPage - 1) * 10 + (i + 1),
      width: "56px",
      style: { color: "#9aa5b4", fontFamily: "monospace", fontSize: 12 },
    },
    {
      name: "Presentation Title",
      cell: (row) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: "#1a2332", lineHeight: 1.35, marginBottom: 3 }}>
            {row.presentation_title?.length > 55
              ? row.presentation_title.slice(0, 55) + "…"
              : row.presentation_title || <em style={{ color: "#c8d0db" }}>Untitled</em>}
          </div>
          <div style={{ fontSize: 12, color: "#1a6b3c", fontWeight: 600 }}>
            {row.participant_name}
          </div>
        </div>
      ),
      sortable: true,
      selector: (row) => row.title,
      grow: 3,
    },
    {
      name: "Day",
      cell: (row) => (
        <span style={S.dayBadge(row.day)}>Day {row.day}</span>
      ),
      sortable: true,
      selector: (row) => row.day,
      width: "90px",
      center: true,
    },
    {
      name: "Name",
      cell: (row) => (
        <span style={S.categoryBadge(row.participant_name)}>{row.participant_name}</span>
      ),
      sortable: true,
      selector: (row) => row.participant_name,
      width: "120px",
    },
    {
      name: "Category",
      cell: (row) => (
        <span style={S.categoryBadge(row.category)}>{row.category}</span>
      ),
      sortable: true,
      selector: (row) => row.category,
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex" }}>
          <button
            style={S.editBtn}
            onClick={()=> handleEditPresentation(row)}
          >
            ✎ Edit
          </button>
          <button
            style={S.deleteBtn}
            onClick={() => { setSelectedRow(row); setShowDelete(true); }}
          >
            ✕ Delete
          </button>
        </div>
      ),
      width: "168px",
      right: true,
    },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100" style={S.page}>
        <Header />
        <div style={S.container}>

          {/* ── Top bar ── */}
          <div style={S.topBar}>
            <div>
              <div style={S.breadcrumb}>Conference Program</div>
              <h2 style={S.pageTitle}>Presentation Setup</h2>
            </div>
            <button
              style={S.addBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#2d8a55";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(26,107,60,0.28)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#1a6b3c";
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
              onClick={
                handleAddPresentation
              }
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              Add Presentation
            </button>
          </div>

          {/* ── Filter chips ── */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {/* Day chips */}
            {["", "1", "2"].map((d) => (
              <button
                key={`day-${d}`}
                onClick={() => setDayFilter(d)}
                style={{
                  padding: "5px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                  cursor: "pointer", border: "1.5px solid",
                  fontFamily: "'DM Sans', sans-serif", transition: "all 0.12s",
                  borderColor: dayFilter === d ? "#1a6b3c" : "#e3e8ef",
                  background:  dayFilter === d ? "#1a6b3c" : "#fff",
                  color:       dayFilter === d ? "#fff"    : "#6b7a8d",
                }}
              >
                {d === "" ? "All Days" : `Day ${d}`}
              </button>
            ))}

            <span style={{ width: 1, height: 20, background: "#e3e8ef", margin: "0 4px" }} />

            {/* Category chips */}
            {["", ...availableCategories].map((c) => (
              <button
                key={`cat-${c}`}
                onClick={() => setCatFilter(c)}
                style={{
                  padding: "5px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                  cursor: "pointer", border: "1.5px solid",
                  fontFamily: "'DM Sans', sans-serif", transition: "all 0.12s",
                  borderColor: catFilter === c ? "#1a6b3c" : "#e3e8ef",
                  background:  catFilter === c ? "#1a6b3c" : "#fff",
                  color:       catFilter === c ? "#fff"    : "#6b7a8d",
                }}
              >
                {c === "" ? "All Categories" : c}
              </button>
            ))}
          </div>

          {/* ── Table card ── */}
          <div style={S.tableCard}>
            <div style={S.tableToolbar}>
              <div style={S.toolbarLeft}>
                <span style={S.tableTitle}>All Presentations</span>
                {!loading && (
                  <span style={S.countBadge}>{filteredData.length}</span>
                )}
              </div>
              <div style={S.searchWrap}>
                <span style={S.searchIcon}>⌕</span>
                <input
                  style={S.searchInput}
                  type="text"
                  placeholder="Search presentation title"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={(e) => (e.target.style.borderColor = "#1a6b3c")}
                  onBlur={(e) => (e.target.style.borderColor = "#e3e8ef")}
                />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              onChangePage={(p) => setCurrentPage(p)}
              progressPending={loading}
              progressComponent={<TableSkeleton />}
              customStyles={tableStyles}
              noDataComponent={
                <div style={S.emptyState}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>No presentations found</div>
                  <div style={{ fontSize: 13 }}>
                    {searchText || dayFilter || catFilter
                      ? "Try adjusting your filters"
                      : "Add your first presentation to get started"}
                  </div>
                </div>
              }
            />
          </div>
        </div>
        <Footer />
      </div>

      {/* ── Delete Modal ── */}
      <Modal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        centered
        contentClassName="border-0"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        <Modal.Body style={{ padding: "32px 28px", textAlign: "center" }}>
          <div style={S.deleteIcon}>🗑</div>
          <h5 style={{ fontWeight: 700, color: "#1a2332", marginBottom: 8 }}>
            Delete Presentation
          </h5>
          <p style={{ color: "#6b7a8d", fontSize: 14, marginBottom: 24 }}>
            Are you sure you want to delete{" "}
            <strong style={{ color: "#1a2332" }}>
              {selectedRow?.presentation_title?.slice(0, 48)}
              {selectedRow?.presentation_title?.length > 48 ? "…" : ""}
            </strong>
            ?<br />
            This action cannot be undone.
          </p>
          <div style={S.modalActionRow}>
            <button style={S.modalCancelBtn} onClick={() => setShowDelete(false)}>
              Cancel
            </button>
            <button style={S.modalDeleteBtn} onClick={handleDelete}>
              Yes, Delete
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PresentationSetupList;